import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { Modal } from '@src/elements/modal/modal'
import { Button } from '@src/elements/button/button'
import { IntersectionsPopup } from '@src/components/intersections-popup/intersections-popup'
import { DatePicker } from '@src/elements/input/date-picker'
import { Spacer } from '@src/elements/spacer/spacer'

import Member from '@src/data/member'
import { HolderType } from '@src/data/workout-data'
import { ExerciseResponse } from '@src/data/exercise-response'
import { WorkoutExercise } from '@src/data/workout-exercise'
import { MemberTrainingPlanDTO } from '@src/routes/content/components/training-plan-builder/save-plan'
import TrainingPlan from '@src/data/training-plan'
import { NewActiveTrainingWorkouts } from '@src/state/store'

import EventBus from '@src/lib/event_bus'
import { WorkoutService } from '@src/service/workout'
import camelcaseKeys from 'camelcase-keys'
import { TrainingPlanService } from '@src/service/training-plan'
import { MemberProfile } from '@src/data/member-profile'
import { convertLocalDateToDate, convertToLocalDate, convertToYYYYMMDD } from '@src/util/date-format'
import SessionManager from '@src/lib/session_manager'
import { ReviewPopup } from '@src/components/review-popup/review-popup'
import { defaultTrainingPlanFilters } from '@src/state/dafaults'

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember || this.$store.state.memberProfile
    },
    newActiveTrainingWorkouts () {
      return this.$store.state.newActiveTrainingWorkouts
    }
  }
})
export class ActiveTrainingWorkout extends Vue {

  @Prop({ required: true })
  protected title: string

  @Prop({ required: true })
  protected noSelectionPlaceholder: string

  @Prop({ required: false })
  protected content: any

  @Prop({ required: false })
  protected activeItem: any

  @Prop({ required: true })
  protected type: 'custom' | 'home' | 'training-plan'

  protected workoutService: WorkoutService = new WorkoutService()
  protected trainingPlanService: TrainingPlanService = new TrainingPlanService()

  protected showActivePopup: boolean = false
  protected currentActiveItem: any
  protected member: Member | MemberProfile
  protected newActiveTrainingWorkouts: NewActiveTrainingWorkouts

  protected emptyStartDateActiveTraining: boolean = false
  protected startDateActiveTraining: string = ''
  protected emptyEndDateActiveTraining: boolean = false
  protected endDateActiveTraining: string = ''

  protected showIntersectionsTrainingPlanPopup: boolean = false
  protected overwritingTrainingPlan: MemberTrainingPlanDTO
  protected intersections: TrainingPlan[] = []
  protected showReviewTrainingPlanPopup: boolean = false
  private disableSaveButton: boolean = false

  constructor () {
    super()
    this.showActivePopup = false
  }

  showHideModal (type?: string, id?: number) {
    if (this.type === type && id) {
      this.showActivePopup = true
      this.$forceUpdate()
    } else if (!id) {
      this.showActivePopup = !this.showActivePopup
      if (this.showActivePopup === false) {
        this.clearModalData()
      }
    }
  }

  clearModalData () {
    this.$store.dispatch('filterWorkouts', { name: '' })
    this.$store.dispatch('filterWorkouts', { type: undefined })
    this.$store.dispatch('filterTrainingPlans', defaultTrainingPlanFilters())
    this.$store.dispatch('updateActiveTrainingWorkouts', {
      newCustomWorkoutId: 0,
      newHomeWorkoutId: 0,
      newTrainingPlanId: 0,
      newTrainingPlanName: '',
      newTrainingPlanTotalDays: 0
    })
    this.startDateActiveTraining = ''
    this.endDateActiveTraining = ''
    this.emptyStartDateActiveTraining = false
    this.emptyEndDateActiveTraining = false
  }

  @Watch('newActiveTrainingWorkouts.newTrainingPlanId')
  changeSelectedTrainingPlan () {
    if (this.type === 'training-plan' && this.member && this.newActiveTrainingWorkouts.newTrainingPlanId && this.startDateActiveTraining) {
      this.computeEndDateTrainingPlan()
    }
  }

  beforeMount () {
    this.currentActiveItem = this.activeItem

    EventBus.on('expandMemberTraining', this.showHideModal)
    this.$watch('activeItem', (activeItem) => {
      this.currentActiveItem = activeItem
    })
  }

  async confirmSelection () {
    this.disableSaveButton = true
    if (this.type === 'custom') {
      if (this.member && this.newActiveTrainingWorkouts.newCustomWorkoutId) {
        if (await this.assignWorkout(this.member.id, this.newActiveTrainingWorkouts.newCustomWorkoutId)) {

          // reload to show the newly activated workout
          EventBus.trigger('reloadSelectedMember')
          this.$store.dispatch('showToast', {
            message: this.$t('PAGE.CONTENT.WORKOUT.ASSIGN_NOTIFICATION', { name: (this.member ? this.member.fullName : /* istanbul ignore next: the member is checked above */ '') }),
            fadeOut: true
          })
          this.showHideModal()
        }
      }
    } else if (this.type === 'home') {
      if (this.member && this.newActiveTrainingWorkouts.newHomeWorkoutId) {
        if (await this.assignWorkout(this.member.id, this.newActiveTrainingWorkouts.newHomeWorkoutId)) {

          // reload to show the newly activated workout
          EventBus.trigger('reloadSelectedMember')
          // successful assign
          this.$store.dispatch('showToast', {
            message: this.$t('PAGE.CONTENT.WORKOUT.ASSIGN_NOTIFICATION', { name: (this.member ? this.member.fullName : /* istanbul ignore next: the member is checked above */ '') }),
            fadeOut: true
          })
          this.showHideModal()
        }
      }
    } else {
      if (!this.startDateActiveTraining || !this.endDateActiveTraining) {
        // validate dates
        this.emptyStartDateActiveTraining = true
        this.emptyEndDateActiveTraining = true
        this.disableSaveButton = false
        return null
      } else if (this.member && this.newActiveTrainingWorkouts.newTrainingPlanId && this.newActiveTrainingWorkouts.newTrainingPlanName && this.newActiveTrainingWorkouts.newTrainingPlanTotalDays) {
        // assign training plan to member
        const tp = await this.assignTrainingPlan(this.member, this.startDateActiveTraining)
        this.disableSaveButton = false
        return tp
      }
    }
    this.disableSaveButton = false
  }

  async assignWorkout (memberId: number, workoutId: number): Promise<boolean> {
    // get the workout details
    const workoutResponse: any = await this.workoutService.getWorkout(workoutId)

    if (!workoutResponse) {
      return false
    }

    if (workoutResponse.workout.holder_type === HolderType.MEMBER) {
      const activatedWorkoutId = await this.workoutService.activateWorkout(workoutResponse.workout.id, memberId)
      if (!activatedWorkoutId) return false
      this.$store.dispatch('setCheckupWorkout', activatedWorkoutId)
      return true
    }

    let exercises: any[] = []
    if (workoutResponse.workout && workoutResponse.workout.workout_exercises) {
      exercises = workoutResponse.workout.workout_exercises.map((workoutExercise) => {
        const exercise = new ExerciseResponse(camelcaseKeys(workoutExercise.exercise, { deep: true }))
        let formattedWorkoutExercise: WorkoutExercise = new WorkoutExercise(camelcaseKeys(workoutExercise, { deep: true }))
        formattedWorkoutExercise.setExercise(exercise)
        return formattedWorkoutExercise
      })
    }

    // create the member owned workout copy
    const memberWorkout = this.workoutService.buildWorkout(camelcaseKeys(workoutResponse.workout), exercises, this.$store.state.club.screenExerciseLen, this.$store.state.stations,
      HolderType.MEMBER, this.$store.state.club.id, memberId)

    const newWorkoutId = await this.workoutService.add(memberWorkout, workoutId)

    if (!newWorkoutId) {
      return false
    }

    // activate workout
    const activatedWorkoutId = await this.workoutService.activateWorkout(newWorkoutId, memberId)

    if (!activatedWorkoutId) {
      return false
    }

    this.$store.dispatch('setCheckupWorkout', activatedWorkoutId)
    return true
  }

  async assignTrainingPlan (member: Member, startDate: string) {

    // get training plan details
    let trainingPlan: TrainingPlan | null = await this.trainingPlanService.getTrainingPlan(this.newActiveTrainingWorkouts.newTrainingPlanId)
    if (!trainingPlan) {
      return null
    }

    // assign to member
    const assignResponse = await new TrainingPlanService()
      .assignTrainingPlanToMember(this.newActiveTrainingWorkouts.newTrainingPlanName, convertToYYYYMMDD(startDate),
        this.formatTrainingDays(trainingPlan.trainingDays), member.id, trainingPlan.holderType !== HolderType.MEMBER)
    if (Object.keys(assignResponse.training_days).length) {
      // failed to assign - plan has deleted workouts
      this.showReviewPopup()
    } else {
      if (assignResponse.intersections.length) {
        // failed to assign: if the plan intersects, show the popup to allow the user to decide the further actions
        this.intersections = assignResponse.intersections
        this.overwritingTrainingPlan = assignResponse.trainingPlanData
        this.showIntersectionsNotificationTrainingPlan()
      } else {
        // successful assign
        EventBus.trigger('reloadSelectedMember')
        this.$store.dispatch('setCheckupTrainingPlan', trainingPlan.id)
        this.$store.dispatch('showToast', {
          message: this.$t('PAGE.CONTENT.TRAINING_PLAN.ASSIGN_NOTIFICATION', { name: (this.member ? this.member.fullName : /* istanbul ignore next: only called with a member */ '') }),
          fadeOut: true
        })
        this.showHideModal()
      }
    }
  }

  showIntersectionsNotificationTrainingPlan () {
    this.showIntersectionsTrainingPlanPopup = true
    this.showActivePopup = false
  }

  showReviewPopup () {
    this.showReviewTrainingPlanPopup = true
    this.showActivePopup = false
  }

  computeStartDateTrainingPlan () {
    if (this.newActiveTrainingWorkouts.newTrainingPlanTotalDays) {
      let selectedDate = convertLocalDateToDate(this.endDateActiveTraining)
      selectedDate.setDate(selectedDate.getDate() - this.newActiveTrainingWorkouts.newTrainingPlanTotalDays + 1)
      let currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)
      this.startDateActiveTraining = selectedDate.getTime() >= currentDate.getTime() ? convertToLocalDate(selectedDate) : ''
      this.checkDatesTrainingPlan()
    }
  }

  computeEndDateTrainingPlan () {
    if (this.newActiveTrainingWorkouts.newTrainingPlanTotalDays) {
      let selectedDate = convertLocalDateToDate(this.startDateActiveTraining)
      selectedDate.setDate(selectedDate.getDate() + this.newActiveTrainingWorkouts.newTrainingPlanTotalDays - 1)
      this.endDateActiveTraining = convertToLocalDate(selectedDate)
      this.checkDatesTrainingPlan()
    }
  }

  checkDatesTrainingPlan () {
    this.emptyStartDateActiveTraining = this.startDateActiveTraining === 'Invalid Date' || !this.startDateActiveTraining
    this.emptyEndDateActiveTraining = this.endDateActiveTraining === 'Invalid Date' || !this.endDateActiveTraining
  }

  formatTrainingDays (days) {
    let trainingDays: any[] = []
    days.forEach(day => {
      trainingDays.push({
        workout_id: day.workout.id,
        rest_days: day.rest_days
      })
    })
    return trainingDays
  }

  render (h: CreateElement) {
    return <div class='active-training-workout-wrapper'>
      {/* modal opened */}
      <Modal class={`active-training-workout`} show={this.showActivePopup} title={this.$t(this.title)} overflow={'hidden'}
             closeCallback={this.showHideModal}>
        <div class='column expanded'>
          {this.content}
        </div>
        <div slot='footer' class='btn-row'>
          {this.type === 'training-plan' ?
            <div class='date-pickers'>
              <DatePicker allowPastDate={false} class={this.emptyStartDateActiveTraining ? 'error' : ''} type='date'
                          value={this.startDateActiveTraining}
                          popper-class={'over-modal'}
                          change={(value: string) => {
                            this.startDateActiveTraining = value.toString()
                            this.computeEndDateTrainingPlan()
                          }} label={`${this.$t('START_DATE')}:`}
                          disabled={!this.newActiveTrainingWorkouts.newTrainingPlanId} />
              <DatePicker allowPastDate={false} class={this.emptyEndDateActiveTraining ? 'error' : ''} type='date'
                          value={this.endDateActiveTraining}
                          popper-class={'over-modal'}
                          change={(value: string) => {
                            this.endDateActiveTraining = value.toString()
                            this.computeStartDateTrainingPlan()
                          }} label={`${this.$t('END_DATE')}:`}
                          disabled={!this.newActiveTrainingWorkouts.newTrainingPlanId} />
            </div>
            : ''}
          <Button label={this.$t('CANCEL')} click={this.showHideModal} type='outline' />
          <Button class='confirm-btn' label={this.$t('SAVE')} click={this.confirmSelection} type='elevated'
                  disabled={(!this.newActiveTrainingWorkouts.newTrainingPlanId && !this.newActiveTrainingWorkouts.newCustomWorkoutId)
                  || this.disableSaveButton} />
        </div>
      </Modal>

      {/* active item card */}
      {this.currentActiveItem ?
        <div class='active-training-workout'>{this.currentActiveItem}</div>

        : <div class='active-training-workout empty'>
          <div>{this.$t(this.noSelectionPlaceholder)}</div>
          <Spacer />
          {this.checkIfPermissionsToCreate(h)}
        </div>
      }

      {this.showIntersectionsTrainingPlanPopup ?
        <IntersectionsPopup show={true}
                            closeCallback={this.closeIntersectionsPopup}
                            member={this.member}
                            intersections={this.intersections}
                            overwritingTrainingPlan={this.overwritingTrainingPlan} /> : null}
      {this.showReviewTrainingPlanPopup ?
        <ReviewPopup show={true}
                     trainingPlanId = {this.newActiveTrainingWorkouts.newTrainingPlanId}
                     member={this.member}
                     startDate={this.startDateActiveTraining}
                     closeCallback={this.closeReviewPopup}/> : null}
    </div>
  }

  checkIfPermissionsToCreate (h: CreateElement) {
    const assignButton = <Button class='assign-workout' icon='util/add' label={this.$t('ASSIGN')}
                                 click={this.showHideModal}
                                 type='remove' />

    if (SessionManager.Instance.isManager()) return assignButton

    const allowAssignWorkout = this.member.memberPermissions.changeIndividualStationWorkout || this.member.memberPermissions.changeActiveStationWorkout
    const allowAssignTrainingPlan = this.member.memberPermissions.changeIndividualTrainingPlan || this.member.memberPermissions.changeActiveTrainingPlan
    switch (this.type) {
      case 'custom': return allowAssignWorkout ? assignButton : null
      case 'training-plan': return allowAssignTrainingPlan ? assignButton : null
      default:
        return null
    }
  }

  closeIntersectionsPopup (successfulAssign: boolean) {
    if (successfulAssign) {
      this.showIntersectionsTrainingPlanPopup = false
      this.intersections = []
      this.$store.dispatch('showToast', {
        message: this.$t('PAGE.CONTENT.TRAINING_PLAN.ASSIGN_NOTIFICATION', { name: (this.member ? this.member.fullName : '') }),
        fadeOut: true
      })
    } else {
      this.showIntersectionsTrainingPlanPopup = false
    }
    this.clearModalData()
  }

  closeReviewPopup () {
    this.showReviewTrainingPlanPopup = false
  }

  beforeDestroy () {
    EventBus.off('expandMemberTraining', this.showHideModal)
  }
}
